import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://elmaplo.bayanshop.ma/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// Intercepteur pour ajouter le token d'authentification et gérer les Content-Type
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // LOGIQUE CRITIQUE POUR LES FICHIERS
    if (config.data instanceof FormData) {
      // Pour les fichiers (FormData), on DOIT supprimer le Content-Type 
      // pour laisser le navigateur définir 'multipart/form-data; boundary=...'
      // Si on laisse 'application/json' ou 'application/x-www-form-urlencoded', l'upload échoue (Erreur 500)
      if (config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    } else {
      // Par défaut pour les objets JSON simples
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/**
 * SERVICES DE L'ENTREPRISE (SETTINGS)
 */
export const companyService = {
  getSettings: () => api.get('/company/settings'),

  updateSettings: (formData: FormData) => {
    // Laravel exige POST + _method: PUT pour les formulaires avec fichiers
    if (!formData.has('_method')) {
      formData.append('_method', 'PUT');
    }
    
    return api.post('/company/settings', formData);
  },
};

/**
 * AUTHENTIFICATION
 */
export const authService = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/login', credentials),
  register: (userData: any) => api.post('/register', userData),
  logout: () => api.post('/logout'),
  getUser: () => api.get('/user'),
};

/**
 * PRODUITS
 */
export const productService = {
  getAll: (params?: any) => api.get('/products', { params }),
  getById: (id: number) => api.get(`/products/${id}`),
  create: (data: any) => api.post('/products', data),
  update: (id: number, data: any) => api.put(`/products/${id}`, data),
  delete: (id: number) => api.delete(`/products/${id}`),
  adjustStock: (id: number, data: any) => api.post(`/products/${id}/adjust-stock`, data),
};

/**
 * CLIENTS
 */
export const clientService = {
  getAll: (params?: any) => api.get('/clients', { params }),
  getById: (id: number) => api.get(`/clients/${id}`),
  create: (data: any) => api.post('/clients', data),
  update: (id: number, data: any) => api.put(`/clients/${id}`, data),
  delete: (id: number) => api.delete(`/clients/${id}`),
};

/**
 * DEVIS
 */
export const quoteService = {
  getAll: (params?: any) => api.get('/quotes', { params }),
  getById: (id: number) => api.get(`/quotes/${id}`),
  create: (data: any) => api.post('/quotes', data),
  update: (id: number, data: any) => api.put(`/quotes/${id}`, data),
  delete: (id: number) => api.delete(`/quotes/${id}`),
  updateStatus: (id: number, status: string) => api.patch(`/quotes/${id}/status`, { status }),
  convertToInvoice: (id: number) => api.post(`/quotes/${id}/convert-to-invoice`),
  generatePdf: (id: number) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
};

/**
 * FACTURES
 */
export const invoiceService = {
  getAll: (params?: any) => api.get('/invoices', { params }),
  getById: (id: number) => api.get(`/invoices/${id}`),
  create: (data: any) => api.post('/invoices', data),
  update: (id: number, data: any) => api.put(`/invoices/${id}`, data),
  delete: (id: number) => api.delete(`/invoices/${id}`),
  updateStatus: (id: number, status: string) => api.patch(`/invoices/${id}/status`, { status }),
  generatePdf: (id: number) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  generatePdfCachet: (id: number) => api.get(`/invoices/${id}/pdfcachet`, { responseType: 'blob' }),
  generateExcel: (id: number) => api.get(`/invoices/${id}/excel`, { responseType: 'blob' }),
};

/**
 * TABLEAU DE BORD
 */
export const dashboardService = {
  getStats: () => api.get('/dashboard'),
};

/**
 * FOURNISSEURS
 */
export const supplierService = {
  getAll: (params?: any) => api.get('/suppliers', { params }),
  getById: (id: number) => api.get(`/suppliers/${id}`),
  create: (data: any) => api.post('/suppliers', data),
  update: (id: number, data: any) => api.put(`/suppliers/${id}`, data),
  delete: (id: number) => api.delete(`/suppliers/${id}`),
};

/**
 * BONS DE LIVRAISON
 */
export const deliveryNoteService = {
  getAll: (params?: any) => api.get('/delivery-notes', { params }),
  getById: (id: number) => api.get(`/delivery-notes/${id}`),
  create: (data: any) => api.post('/delivery-notes', data),
  update: (id: number, data: any) => api.put(`/delivery-notes/${id}`, data),
  delete: (id: number) => api.delete(`/delivery-notes/${id}`),
  updateStatus: (id: number, status: string) => api.patch(`/delivery-notes/${id}/status`, { status }),
  generatePdf: (id: number) => api.get(`/delivery-notes/${id}/pdf`, { responseType: 'blob' }),
};

/**
 * RÈGLEMENTS
 */
export const paymentService = {
  getAll: (params?: any) => api.get('/payments', { params }),
  getById: (id: number) => api.get(`/payments/${id}`),
  create: (data: any) => api.post('/payments', data),
  update: (id: number, data: any) => api.put(`/payments/${id}`, data),
  delete: (id: number) => api.delete(`/payments/${id}`),
};

/**
 * CARNETS DE CHÈQUES ET CHÈQUES
 */
export const checkbookService = {
  getAll: (params?: any) => api.get('/checkbooks', { params }),
  getById: (id: number) => api.get(`/checkbooks/${id}`),
  create: (data: any) => api.post('/checkbooks', data),
  update: (id: number, data: any) => api.put(`/checkbooks/${id}`, data),
  delete: (id: number) => api.delete(`/checkbooks/${id}`),
};

export const checkService = {
  getAll: (params?: any) => api.get('/checks', { params }),
  getById: (id: number) => api.get(`/checks/${id}`),
  update: (id: number, data: any) => api.put(`/checks/${id}`, data),
  updateStatus: (id: number, status: string) => api.patch(`/checks/${id}/status`, { status }),
};

/**
 * CAUTIONS
 */
export const cautionService = {
  getAll: (params?: any) => api.get('/cautions', { params }),
  getById: (id: number) => api.get(`/cautions/${id}`),
  create: (data: any) => api.post('/cautions', data),
  update: (id: number, data: any) => api.put(`/cautions/${id}`, data),
  delete: (id: number) => api.delete(`/cautions/${id}`),
  updateStatus: (id: number, status: string) => api.patch(`/cautions/${id}/status`, { status }),
};

export const changepassword = {
  update: (data: any) => api.post('/change-password', data)
};